/**
 * OSRM kullanarak gerçek yol mesafesini hesaplar
 * Haversine'den farklı olarak gerçek yol ağını kullanır
 */

const { routingServiceURL } = require('../../networking/api');
const { haversineDistance } = require('./haversineDistance');

// Basit in-memory cache (aynı koordinatlar için tekrar API çağırma)
const distanceCache = new Map();
const CACHE_SIZE_LIMIT = 1000; // Maksimum 1000 kayıt

/**
 * Cache key oluşturur (koordinatları 4 ondalık basamağa yuvarlar)
 */
function getCacheKey(coords1, coords2) {
  const lat1 = coords1.lat.toFixed(4);
  const lng1 = coords1.lng.toFixed(4);
  const lat2 = coords2.lat.toFixed(4);
  const lng2 = coords2.lng.toFixed(4);
  return `${lat1},${lng1}|${lat2},${lng2}`;
}

/**
 * OSRM API'den gerçek yol mesafesini alır
 * @param {Object} coords1 - Başlangıç koordinatı {lat, lng}
 * @param {Object} coords2 - Bitiş koordinatı {lat, lng}
 * @returns {Promise<number>} - Mesafe (km cinsinden)
 */
async function fetchRouteDistance(coords1, coords2) {
  try {
    // DİKKAT: OSRM longitude,latitude formatı kullanır!
    const url = `${routingServiceURL}/route/v1/driving/${coords1.lng},${coords1.lat};${coords2.lng},${coords2.lat}?overview=false`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      // Timeout: 3 saniye
      signal: AbortSignal.timeout(3000)
    });

    if (!response.ok) {
      throw new Error(`OSRM API error: ${response.status}`);
    }

    const data = await response.json();

    if (data.code !== 'Ok' || !data.routes || data.routes.length === 0) {
      throw new Error('No route found');
    }

    // Mesafe metre cinsinden gelir, km'ye çevir
    const distanceInMeters = data.routes[0].distance;
    const distanceInKm = distanceInMeters / 1000;

    return distanceInKm;

  } catch (error) {
    // API hatası durumunda null döndür (fallback için)
    console.warn('OSRM API error:', error.message);
    return null;
  }
}

/**
 * Gerçek yol mesafesini hesaplar (cache + API + fallback)
 * @param {Object} coords1 - Başlangıç koordinatı {lat, lng}
 * @param {Object} coords2 - Bitiş koordinatı {lat, lng}
 * @param {boolean} useFallback - API başarısızsa haversine kullan (default: true)
 * @returns {Promise<number>} - Mesafe (km cinsinden)
 */
async function realRouteDistance(coords1, coords2, useFallback = true) {
  // Validation: Koordinatlar geçerli mi?
  if (!coords1?.lat || !coords1?.lng || !coords2?.lat || !coords2?.lng) {
    return useFallback ? haversineDistance(coords1, coords2) : Infinity;
  }

  // Validation: 0,0 koordinatları (GPS verisi yok)
  if ((coords1.lat === 0 && coords1.lng === 0) || (coords2.lat === 0 && coords2.lng === 0)) {
    return useFallback ? haversineDistance(coords1, coords2) : Infinity;
  }

  // 1. Cache'de var mı kontrol et
  const cacheKey = getCacheKey(coords1, coords2);
  if (distanceCache.has(cacheKey)) {
    return distanceCache.get(cacheKey);
  }

  // 2. OSRM API'den gerçek mesafeyi al
  const routeDistance = await fetchRouteDistance(coords1, coords2);

  // 3a. API başarılıysa sonucu cache'le ve döndür
  if (routeDistance !== null) {
    // Cache boyutu kontrolü (basit FIFO)
    if (distanceCache.size >= CACHE_SIZE_LIMIT) {
      const firstKey = distanceCache.keys().next().value;
      distanceCache.delete(firstKey);
    }

    distanceCache.set(cacheKey, routeDistance);
    return routeDistance;
  }

  // 3b. API başarısızsa fallback kullan
  if (useFallback) {
    const haversine = haversineDistance(coords1, coords2);

    // Haversine sonucunu da cache'le (API düzelene kadar)
    distanceCache.set(cacheKey, haversine);
    return haversine;
  }

  // Fallback kullanılmayacaksa Infinity döndür
  return Infinity;
}

/**
 * Cache'den mesafe okur (API çağırmaz)
 * @param {Object} coords1 - Başlangıç koordinatı {lat, lng}
 * @param {Object} coords2 - Bitiş koordinatı {lat, lng}
 * @returns {number|null} - Cache'de varsa mesafe (km), yoksa null
 */
function getCachedDistance(coords1, coords2) {
  // Validation
  if (!coords1?.lat || !coords1?.lng || !coords2?.lat || !coords2?.lng) {
    return null;
  }

  const cacheKey = getCacheKey(coords1, coords2);
  return distanceCache.has(cacheKey) ? distanceCache.get(cacheKey) : null;
}

/**
 * Cache'i temizler (test/debug için)
 */
function clearDistanceCache() {
  distanceCache.clear();
}

/**
 * Cache durumunu döndürür (debug için)
 */
function getCacheStats() {
  return {
    size: distanceCache.size,
    limit: CACHE_SIZE_LIMIT
  };
}

module.exports = {
  realRouteDistance,
  getCachedDistance,
  clearDistanceCache,
  getCacheStats
};
